import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

interface Service {
  id: string;
  service_number: string;
  service_type: string;
  status: string;
  amount: number;
  created_at: string;
  client: {
    full_name: string;
    email: string;
  };
}

export function ServicesList() {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select(`
          *,
          client:profiles!services_client_id_fkey (
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        // Ensure the data matches our Service interface
        const typedServices = data.map(service => ({
          ...service,
          client: {
            full_name: service.client?.full_name || 'Unknown',
            email: service.client?.email || 'No email'
          }
        })) as Service[];
        
        setServices(typedServices);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch services');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-4">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {services.map((service) => (
        <Card key={service.id}>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium">Service Number</p>
                <p className="text-sm">{service.service_number}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Client</p>
                <p className="text-sm">{service.client.full_name}</p>
                <p className="text-sm text-muted-foreground">{service.client.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Type</p>
                <p className="text-sm">{service.service_type}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Status</p>
                <p className="text-sm">{service.status}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}